import { useEffect, useRef, useState } from "react";

import { isEqual } from "lodash";
import { Keyboard, ScrollView, View } from "react-native";
import { Button, Card, Text, TextInput } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import OptionChips from "./OptionChips";
import {
  InfoBanner,
  KeyboardAwareView,
  TextInputHelperText,
} from "../../../../components";
import useProductFormFormik from "../../hooks/useProductFormFormik";
import { Variant, Option, VariantWithOptions } from "../../types";

type Props = {
  variantWithOptions?: VariantWithOptions;
  selectedVariant?: string;
  existingVariants: Variant[];
  saveVariant: (vo: VariantWithOptions) => void;
};

export default function VariantForm({
  variantWithOptions,
  selectedVariant,
  existingVariants,
  saveVariant,
}: Props) {
  const [variant, setVariant] = useState(
    variantWithOptions?.variant?.name || ""
  );
  const [options, setOptions] = useState(variantWithOptions?.options || []);
  const [optionInput, setCurrentOption] = useState("");

  const { setStatus, status } = useProductFormFormik();

  const scrollViewRef = useRef<ScrollView>(null);
  const tailwind = useTailwind();

  const isKeyboardOpen = useKeyboardListener();

  useEffect(() => {
    const variantIsDirty =
      variant != variantWithOptions?.variant?.name ||
      !isEqual(options, variantWithOptions?.options);

    setStatus({ ...status, variantIsDirty });
  }, [variant, options]);

  const optionHasError = options.map((o) => o.name).includes(optionInput);

  const variantHasError =
    selectedVariant !== variant &&
    existingVariants.map((v) => v.name).includes(variant);

  const addOption = (name: string) => {
    setOptions((prev) => [...prev, { name }]);
    setCurrentOption("");
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  const removeOption = (opt: Option) =>
    setOptions((prev) => prev.filter((p) => p !== opt));

  return (
    <KeyboardAwareView>
      <ScrollView keyboardShouldPersistTaps="handled" ref={scrollViewRef}>
        <InfoBanner text="Crea opciones para tus variantes aquí. Recuerda que al borrar o cambiar opciones debes revisar el inventario de cada combinación." />

        <Card style={tailwind("m-3 rounded-xl")}>
          <Card.Content>
            <Text variant="titleMedium">Nombre de la variante</Text>
            <TextInput
              value={variant}
              onChangeText={setVariant}
              mode="outlined"
              placeholder="Ej.: talla, color"
              error={variantHasError}
              onFocus={scrollToBottom}
            />
            <TextInputHelperText
              error={variantHasError ? "Variante ya existe" : undefined}
            />
          </Card.Content>

          <Card.Content style={tailwind("mt-4")}>
            <Text variant="titleMedium">Opciones</Text>

            <View style={tailwind("flex-row justify-between")}>
              <View style={tailwind("flex-1")}>
                <TextInput
                  style={tailwind("mr-2 text-xs h-10")}
                  value={optionInput}
                  onChangeText={setCurrentOption}
                  mode="outlined"
                  onFocus={scrollToBottom}
                  placeholder="Ej.: rojo, azul"
                  error={optionHasError}
                />
                <TextInputHelperText
                  error={optionHasError ? "Opción ya existe" : undefined}
                />
              </View>

              <Button
                mode="contained-tonal"
                icon="plus"
                onPress={() => {
                  addOption(optionInput);
                  scrollToBottom();
                }}
                style={tailwind("self-start")}
                disabled={!optionInput || optionHasError}
              >
                Agregar opción
              </Button>
            </View>

            <OptionChips options={options} removeOption={removeOption} />
          </Card.Content>
        </Card>
      </ScrollView>
      <Button
        mode="contained"
        style={tailwind(`mx-6 ${isKeyboardOpen ? "mb-2" : "mb-8"}`)}
        onPress={() => saveVariant({ variant: { name: variant }, options })}
        disabled={!variant || !options.length}
      >
        {selectedVariant ? "Guardar" : "Crear variante"}
      </Button>
    </KeyboardAwareView>
  );
}

function useKeyboardListener() {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const show = Keyboard.addListener("keyboardWillShow", () =>
      setIsOpen(true)
    );
    const hide = Keyboard.addListener("keyboardWillHide", () =>
      setIsOpen(false)
    );

    return () => {
      show.remove();
      hide.remove();
    };
  }, []);
  return isOpen;
}
